/* eslint-disable react/prop-types */

import React, { useState, useRef, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Select, { components } from 'react-select';
import { Form, Col } from 'react-bootstrap';
import { FormLabel } from './FormLabel';
import ErrorFeedback from './ErrorFeedback';
import { getValue } from 'helpers/utils';

const CustomValueContainer = ({ children, ...props }) => {
  const value = props.getValue();
  const isMulti = props.selectProps.isMulti;
  const istextAndUnit = props.istextAndUnit;
  return (
    <components.ValueContainer {...props}>
      {isMulti && value.length > 2 ? (
        <span>{`${value.length} options selected`}</span>
      ) : (
        children
      )}
    </components.ValueContainer>
  );
};

const DynamicDropdown = ({
  metadata,
  control,
  errors,
  isDisabled,
  istextAndUnit,
  fetchtextAndUnit,
  formMetadata,
  onChange // NEW: Add onChange prop
}) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const selectRef = useRef(null);
  const { setValue, getValues, watch, reset } = useFormContext();
  const formValues = watch(); // Watch all form values
  const dependentFieldValue = getValue(metadata.dependsOn, formValues);

  if (istextAndUnit == true) {
    console.log(Object.values(metadata?.defaultValue)[1], 'metadatametadata');
  }

  // Handle exclude_selected_from: true
  const formState = getValues();
  const fieldsToExcludeFrom = Object.values(formMetadata?.fields || {}).filter(
    f =>
      f.id !== metadata.id &&
      f.exclude_selected_from &&
      metadata.exclude_selected_from
  );

  let excludedValues = [];
  fieldsToExcludeFrom.forEach(f => {
    const selected = formState[f.id];
    if (Array.isArray(selected)) {
      excludedValues.push(...selected);
    } else if (selected) {
      excludedValues.push(selected);
    }
  });

  const filteredOptions = (metadata.options || []).filter(opt => {
    return !excludedValues.includes(opt.value);
  });

  // Transform options to grouped Select format - memoized to prevent unnecessary recalculations
  const formatGroupedOptions = React.useCallback(options => {
    if (!options) return [];

    // Check if the options are grouped (have sectionLabel)
    if (options[0]?.sectionLabel) {
      return options.map(group => ({
        label: group.sectionLabel,
        options: group.options.map(option => ({
          value: option.value,
          label: option.label
        }))
      }));
    }

    // Return regular options if not grouped
    return options.map(option => ({
      value: option.value,
      label: option.label
    }));
  }, []);

  const selectOptions = React.useMemo(
    () => formatGroupedOptions(filteredOptions),
    [formatGroupedOptions, filteredOptions]
  );

  // Helper to flatten options
  const flattenOptions = React.useCallback(options => {
    return options.reduce((acc, group) => {
      if (group.options) {
        return [...acc, ...group.options];
      }
      return [...acc, group];
    }, []);
  }, []);

  const flatOptions = React.useMemo(
    () => flattenOptions(selectOptions),
    [flattenOptions, selectOptions]
  );

  // Helper to format value based on single/multi select - memoize result to prevent unnecessary re-renders
  const formatValue = React.useCallback(
    (options, value) => {
      if (!value) return metadata.multiSelect ? [] : null;
      const findOption = val => flatOptions.find(opt => opt.value === val);
      let returnValue = metadata.multiSelect
        ? (Array.isArray(value) ? value : [value])
            .map(findOption)
            .filter(Boolean)
        : findOption(value);
      if (typeof fetchtextAndUnit === 'function') {
        // Only call this if the value actually exists
        const foundOption = findOption(value);
        if (foundOption) {
          fetchtextAndUnit(foundOption);
        }
      }
      return returnValue;
    },
    [metadata.multiSelect, flatOptions, fetchtextAndUnit]
  );

  // Handle section header click - memoized to prevent unnecessary recreations
  const handleSectionClick = React.useCallback(
    (group, value, onChangeHandler) => {
      if (!metadata.multiSelect) return;
      const groupValues = group.options.map(option => option.value);

      const isGroupSelected = groupValues.every(val =>
        Array.isArray(value) ? value.includes(val) : false
      );

      const newValue = isGroupSelected
        ? value.filter(val => !groupValues.includes(val))
        : [...(Array.isArray(value) ? value : []), ...groupValues];

      onChangeHandler(newValue);
    },
    [metadata.multiSelect]
  );

  // Handle "Select All" functionality - memoized to prevent unnecessary recreations
  const handleSelectAllClick = React.useCallback(
    (value, onChangeHandler) => {
      if (!metadata.multiSelect) return;
      const allValues = flatOptions.map(option => option.value);

      const isAllSelected = allValues.every(val =>
        Array.isArray(value) ? value.includes(val) : false
      );

      const newValue = isAllSelected ? [] : allValues;
      onChangeHandler(newValue);
    },
    [metadata.multiSelect, flatOptions]
  );

  // Handle click outside - memoized to prevent unnecessary recreations
  const handleClickOutside = React.useCallback(event => {
    if (selectRef.current && !selectRef.current.contains(event.target)) {
      setMenuIsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Effect to handle auto-selection of single options and dependent fields
  const previousDependentValueRef = useRef(dependentFieldValue);
  const initialSetupDoneRef = useRef(false);

  useEffect(() => {
    // Only run once for auto-selecting single options
    if (
      !initialSetupDoneRef.current &&
      !metadata.multiSelect &&
      flatOptions.length === 1
    ) {
      const currentValue = getValues(metadata.id);
      const newValue = flatOptions[0].value;

      if (currentValue !== newValue) {
        setValue(metadata.id, newValue);
        // Call onChange if provided
        if (onChange && typeof onChange === 'function') {
          onChange(newValue);
        }
      }
      initialSetupDoneRef.current = true;
    }

    // Handle dependent field changes
    if (metadata.dependsOn) {
      const prevValue = previousDependentValueRef.current;

      // Only perform the reset if the dependent value changed from a value to undefined/null
      if (prevValue && !dependentFieldValue) {
        const currentValue = getValues(metadata.id);
        if (currentValue !== null) {
          // Only reset if not already null
          setValue(metadata.id, null);
          // Call onChange when clearing dependent field
          if (onChange && typeof onChange === 'function') {
            onChange(null);
          }
        }
      }

      // Update ref for next comparison
      previousDependentValueRef.current = dependentFieldValue;
    }
  }, [
    dependentFieldValue,
    onChange,
    metadata.id,
    metadata.multiSelect,
    metadata.dependsOn,
    setValue,
    getValues,
    flatOptions
  ]);

  // Memoize custom styles to prevent recreation on each render
  const customStyles = React.useMemo(
    () => ({
      control: provided => ({
        ...provided,
        minHeight: '80px',
        backgroundColor: 'white',
        ':focus-within': {
          boxShadow:
            'inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 5px var(--falcon-soft-primary) !important;'
        }
      }),
      valueContainer: provided => ({
        ...provided,
        whiteSpace: 'nowrap',
        overflowY: 'auto',
        textOverflow: 'ellipsis',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        alignContent: 'flex-start',
        maxHeight: '90px'
      }),
      multiValue: provided => ({
        ...provided,
        height: 'fit-content'
      }),
      multiValueLabel: provided => ({
        ...provided
      })
    }),
    []
  );

  const selectedValue = getValues(metadata.id);
  const hasSelectedValue = metadata.multiSelect
    ? Array.isArray(selectedValue) && selectedValue.length > 0
    : !!selectedValue;

  return (
    <Col>
      <Form.Group
        as={Col}
        sm={12}
        md={12}
        style={{
          width: istextAndUnit ? '118px' : '',
          marginTop: istextAndUnit ? '7px' : '',
          borderTopLeftRadius: 'inherit'
        }}
        className="mb-3"
      >
        <FormLabel
          label={istextAndUnit ? '' : metadata.label}
          required={istextAndUnit ? '' : metadata?.validation?.required}
        />
        <div ref={selectRef}>
          <Controller
            name={istextAndUnit ? `${metadata.id}1` : metadata.id}
            control={control}
            defaultValue={metadata.multiSelect ? [] : null}
            rules={{
              required: istextAndUnit ? false : metadata.validation?.required
            }}
            render={({
              field: { value, onChange: fieldOnChange, ...field }
            }) => (
              <Select
                {...field}
                isMulti={metadata.multiSelect}
                isDisabled={
                  !metadata.isEditable ||
                  (metadata.dependsOn && !dependentFieldValue) ||
                  isDisabled
                }
                defaultValue={metadata.defaultValue}
                isClearable={istextAndUnit ? false : true}
                styles={customStyles}
                options={[
                  ...(metadata.multiSelect
                    ? [
                        {
                          label: 'Select All',
                          value: 'select-all',
                          isSelectAll: true
                        }
                      ]
                    : []),
                  ...selectOptions
                ]}
                value={formatValue(selectOptions, value)}
                onChange={(selectedOptions, actionMeta) => {
                  let newValue;

                  if (metadata.multiSelect) {
                    if (!selectedOptions) {
                      newValue = null;
                      fieldOnChange(newValue);
                      // NEW: Call external onChange
                      if (onChange && typeof onChange === 'function') {
                        onChange(newValue);
                      }
                      return;
                    }

                    const selectedValues = Array.isArray(selectedOptions)
                      ? selectedOptions.map(option => option.value)
                      : [];

                    // Handle "Select All" click
                    if (selectedOptions.some(opt => opt.isSelectAll)) {
                      handleSelectAllClick(value, selectAllValue => {
                        fieldOnChange(selectAllValue);
                        // NEW: Call external onChange
                        if (onChange && typeof onChange === 'function') {
                          onChange(selectAllValue);
                        }
                      });
                    } else {
                      newValue =
                        selectedValues.length === 0 ? null : selectedValues;
                      fieldOnChange(newValue);
                      // NEW: Call external onChange
                      if (onChange && typeof onChange === 'function') {
                        onChange(newValue);
                      }
                    }
                  } else {
                    newValue = selectedOptions ? selectedOptions.value : null;
                    fieldOnChange(newValue);
                    // NEW: Call external onChange
                    if (onChange && typeof onChange === 'function') {
                      onChange(newValue);
                    }
                    setMenuIsOpen(false); // Close menu after single select
                  }
                }}
                classNamePrefix="react-select"
                className={errors[metadata.id] ? 'is-invalid' : ''}
                components={{
                  GroupHeading: props => (
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: '0.9em',
                        color: '#666',
                        cursor: 'pointer'
                      }}
                      onClick={() =>
                        handleSectionClick(props.data, value, sectionValue => {
                          fieldOnChange(sectionValue);
                          // NEW: Call external onChange
                          if (onChange && typeof onChange === 'function') {
                            onChange(sectionValue);
                          }
                        })
                      }
                    >
                      {props.children}
                    </div>
                  ),
                  ValueContainer: CustomValueContainer
                }}
                menuIsOpen={menuIsOpen}
                onMenuOpen={() => setMenuIsOpen(true)}
                onMenuClose={() => setMenuIsOpen(false)}
                closeMenuOnSelect={!metadata.multiSelect}
                hideSelectedOptions={false}
              />
            )}
          />
          {metadata?.show_count_message && hasSelectedValue && (
            <>
              {(metadata?.dependents || []).map(depId => {
                const targetField = formMetadata?.fields?.[depId];
                const options = targetField?.options ?? [];

                return (
                  <div key={depId} className="text-muted small">
                    {Array.isArray(options) && options.length > 0
                      ? `${options.length} ${
                          options.length === 1 ? 'entry' : 'entries'
                        } ready`
                      : `No data found. Try a different ${metadata.label}`}
                  </div>
                );
              })}
            </>
          )}
        </div>
        <ErrorFeedback
          key={metadata.id}
          error={errors[metadata.id]}
          istextAndUnit={istextAndUnit}
        />
      </Form.Group>
    </Col>
  );
};

export default DynamicDropdown;
